import { Button } from '@ctw/shared/components/button';
import { ContentTypeIcon } from '@ctw/shared/components/icons/content-type-icon';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { Tooltip } from '@ctw/shared/components/tooltip';
import {
  RelatedDocumentNarratives,
  useNarrativeForBinary,
} from '@ctw/shared/content/related-documents/related-document-narratives';
import { useResourceModal } from '@ctw/shared/context/resource-modal-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { faChevronRight, faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isEmpty } from 'lodash-es';
import { useState } from 'react';

export interface RelatedDocumentsListProps {
  binaryIds: Array<string>;
  heading?: string;
}

export const RelatedDocumentsList = ({
  binaryIds,
  heading = 'Source Documents',
}: RelatedDocumentsListProps) => {
  if (binaryIds.length === 0) {
    return null;
  }

  return (
    <div className={tw`space-y-2`}>
      <div className={tw`font-semibold text-lg`}>{heading}</div>
      <ul className={tw`m-0 list-none space-y-1 p-0`}>
        {binaryIds.map((binaryId) => (
          <RelatedDocumentItem key={binaryId} binaryId={binaryId} />
        ))}
      </ul>
    </div>
  );
};

const RELATED_DOCUMENT_DETAIL_DISPLAY_FIELDS = ['Date', 'Author'] as const;

export interface RelatedDocumentItemProps {
  binaryId: string;
}

export const RelatedDocumentItem = ({ binaryId }: RelatedDocumentItemProps) => {
  const telemetry = useTelemetry();
  const { openResourceModal } = useResourceModal({
    layout: {
      mode: 'full',
      overflowBehavior: 'hidden',
      fullbleed: true,
      showCloseButton: false,
    },
  });
  const { isLoading, isError, data } = useNarrativeForBinary({ binaryId });
  const canDisplayBinaryLink = !(isLoading || isError) && data !== undefined;
  const hasNarrative = (data?.cda?.content?.sections?.length ?? 0) > 0;
  const displayTitle = data?.title ?? data?.cda?.metadata?.document?.title ?? 'Untitled Document';
  const detailFields = data?.cda?.detailFields
    ?.filter((field) => RELATED_DOCUMENT_DETAIL_DISPLAY_FIELDS.includes(field.label as never))
    .map((field) => `${field.label}: ${field.value}`)
    .join(' | ');

  const isCollapsible = canDisplayBinaryLink && hasNarrative;
  const [isExpanded, setIsExpanded] = useState(true);
  const showsNarrative = isCollapsible && isExpanded;
  const narrativeRegionId = `related-document-narrative-${binaryId}`;

  return (
    <div className={tw`rounded-md border border-border-main shadow-sm`}>
      <div
        className={twx('flex w-full min-w-full items-center', {
          'rounded-b-none border-border-main border-b': showsNarrative,
        })}
      >
        <Button
          type="button"
          variant="link"
          className={tw`flex flex-1 items-center gap-1 hover:opacity-80`}
          ariaLabel={isExpanded ? 'Collapse document' : 'Expand document'}
          ariaExpanded={isExpanded}
          ariaControls={isCollapsible ? narrativeRegionId : undefined}
          disabled={!isCollapsible}
          onClick={() => {
            setIsExpanded((previous) => !previous);
          }}
        >
          <div className={tw`flex w-full items-center gap-3 px-3 py-4`}>
            {isCollapsible && (
              <FontAwesomeIcon
                icon={faChevronRight}
                className={twx('h-4 w-4 shrink-0 text-content-icon transition-transform', {
                  'rotate-90': isExpanded,
                })}
              />
            )}
            <div className={tw`block whitespace-nowrap text-left`}>
              {isLoading && <LoadingSpinner message="Loading document..." centered={true} />}
              {isError && (
                <div className={tw`flex items-center gap-2 text-content-subtle`}>
                  <FontAwesomeIcon icon={faCircleExclamation} className={tw`h-5 w-5`} />
                  <div className={tw`font-medium text-[1rem] tracking-wide `}>
                    Unable to load this document
                  </div>
                </div>
              )}
              {canDisplayBinaryLink && (
                <div className={tw`flex flex-col items-start justify-center`}>
                  <div className={tw`font-medium text-[1rem] text-content-main tracking-wide`}>
                    {displayTitle}
                  </div>
                  {!isEmpty(detailFields) && (
                    <div className={tw`text-content-subtle text-xs leading-tight`}>
                      {detailFields}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </Button>
        <Tooltip
          id="related-document-tooltip"
          content="View source document"
          place="top"
          enabled={canDisplayBinaryLink}
        >
          <Button
            type="button"
            variant="unstyled"
            className={tw`flex shrink-0 items-center justify-center px-4 text-primary-text hover:opacity-80`}
            ariaLabel="View source document"
            disabled={!canDisplayBinaryLink}
            telemetryContext={{ resourceType: 'Binary', resourceId: binaryId }}
            onClick={() =>
              openResourceModal({ telemetry, resourceType: 'Binary', resourceId: binaryId })
            }
          >
            <ContentTypeIcon contentType={data?.contentType ?? ''} className={tw`h-5 w-5`} />
          </Button>
        </Tooltip>
      </div>
      {showsNarrative && <RelatedDocumentNarratives binaryId={binaryId} />}
    </div>
  );
};
